import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { downloadBlob } from '@/lib/downloadFile';

function downloadCodes(codes: string[], workspaceName: string) {
  const text = [
    `Backup codes for ${workspaceName}`,
    'Each code works once. Keep this file private.',
    '',
    ...codes
  ].join('\n');

  downloadBlob({
    blob: new Blob([text], { type: 'text/plain' }),
    originalName: 'backup-codes.txt'
  });
}

export function TwoFactorBackupCodes({
  codes,
  children,
  workspaceName
}: {
  codes: string[];
  children?: React.ReactNode;
  workspaceName: string;
}) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const copy = () => {
    navigator.clipboard.writeText(codes.join('\n')).then(
      () => setCopyState('copied'),
      () => setCopyState('failed')
    );
  };

  return (
    <div className="grid gap-4">
      {/* Session replay must never record the plain codes. */}
      <ul
        data-sentry-block
        aria-label="Backup codes"
        className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-4 font-mono text-sm"
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <p className="text-sm text-muted-foreground">
        Store these codes somewhere private, such as a password manager. Each code works once. You
        will not see them again after you leave this page.
      </p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={copy}>
          {copyState === 'copied' ? 'Copied' : 'Copy codes'}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={() => downloadCodes(codes, workspaceName)}
        >
          Download codes
        </Button>
      </div>
      <ErrorMessage
        errors={copyState === 'failed' ? ['Could not copy the codes. Download them instead.'] : []}
      />
      {children}
    </div>
  );
}
